import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StatusBadge from "@/components/base/StatusBadge";
import { Skeleton } from "@/components/base/Loading";
import { formatFullDate } from "@/lib/format";
import { APPLICATION_STATUS_META } from "@/lib/license";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyApplications, type Application } from "@/pages/apply/hooks/useMyApplications";
import { useApprovalProgress, type ApprovalProgress } from "@/pages/apply/hooks/useApprovalProgress";
import ApprovalProgressStrip from "@/pages/apply/components/ApprovalProgressStrip";
import ApprovalTimeline from "@/pages/apply/components/ApprovalTimeline";
import DelistSuggestions from "@/pages/products/components/DelistSuggestions";
import {
  isDelisted,
  recommendSameCategory,
  useProductCatalogStatus,
  type CatalogStatusEntry,
} from "@/pages/products/hooks/useProductCatalogStatus";

const STEP_KEYS = ["apply.stepSubmit", "apply.stepReview", "apply.stepNotify"];

function stepState(application: Application, index: number): "done" | "active" | "rejected" | "idle" {
  const approved = application.status === "approved";
  const rejected = application.status === "rejected";
  if (index === 0) return "done";
  if (index === 1) {
    if (approved || rejected) return "done";
    return "active";
  }
  // index === 2
  if (approved) return "done";
  if (rejected) return "rejected";
  return "idle";
}

function applicationStatusMeta(status: string, t: (key: string) => string) {
  const base = APPLICATION_STATUS_META[status];
  const labels: Record<string, string> = {
    pending: t("apply.pendingLabel"),
    approved: t("apply.approvedLabel"),
    rejected: t("apply.rejectedLabel"),
  };
  return {
    label: labels[status] ?? base?.label ?? status,
    className: base?.className ?? "bg-background-200 text-foreground-700",
  };
}

interface ApplicationRowProps {
  application: Application;
  delisted: boolean;
  suggestions: CatalogStatusEntry[];
  category: string | null;
  progress: ApprovalProgress | null;
}

function ApplicationRow({ application, delisted, suggestions, category, progress }: ApplicationRowProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const meta = applicationStatusMeta(application.status, t);

  return (
    <article className={`rounded-lg border bg-background-50 p-5 ${delisted ? "border-accent-200" : "border-background-200"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-file-list-3-line text-lg" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">{application.product_name}</h3>
              {delisted && (
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-forbid-2-line" />
                  </span>
                  {t("apply.archivedTitle")}
                </span>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-foreground-500">
              <span className="flex items-center gap-1">
                <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-600">
                  <i className="ri-price-tag-3-line" />
                </span>
                {application.product_version}
              </span>
              <span className="rounded-md bg-background-100 px-2 py-0.5 text-foreground-700">
                {edition(application.edition)}
              </span>
              {application.renew_license_id != null && (
                <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-refresh-line" />
                  </span>
                  {t("apply.renewalTag")}
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-computer-line" />
                </span>
                {t("apply.seatUnit", { count: application.seat_count })}
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-calendar-line" />
                </span>
                {t("apply.submittedAt", { date: formatFullDate(application.created_at) })}
              </span>
            </div>
          </div>
        </div>
        <StatusBadge meta={meta} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        {STEP_KEYS.map((stepKey, index) => {
          const state = stepState(application, index);
          const dotClass =
            state === "done"
              ? "bg-primary-500 text-background-50"
              : state === "active"
                ? "bg-accent-500 text-accent-950"
                : state === "rejected"
                  ? "bg-accent-600 text-background-50"
                  : "bg-background-200 text-foreground-500";
          const lineClass = state === "done" ? "bg-primary-300" : "bg-background-200";
          return (
            <div key={stepKey} className="flex flex-1 items-center gap-2 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${dotClass}`}>
                  {state === "done" ? (
                    <i className="ri-check-line text-xs" />
                  ) : state === "rejected" ? (
                    <i className="ri-close-line text-xs" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="whitespace-nowrap text-[11px] text-foreground-600">{t(stepKey)}</span>
              </div>
              {index < STEP_KEYS.length - 1 && <span className={`mb-5 h-px flex-1 ${lineClass}`} />}
            </div>
          );
        })}
      </div>

      <ApprovalProgressStrip progress={progress} />

      <ApprovalTimeline progress={progress} submittedAt={application.created_at} />

      {delisted && <DelistSuggestions items={suggestions} category={category} />}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        <p className="text-xs text-foreground-500">
          {t("apply.appNo")} APP-{String(application.id).padStart(6, "0")}
          {application.reviewed_at ? ` · ${t("apply.processedAt", { date: formatFullDate(application.reviewed_at) })}` : ""}
        </p>
        {application.reviewer_note && (
          <span className="flex items-center gap-1.5 rounded-md bg-secondary-50 px-3 py-1.5 text-[11px] text-secondary-900">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-chat-3-line" />
            </span>
            {application.reviewer_note}
          </span>
        )}
      </div>
    </article>
  );
}

export default function MyApplications() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useCurrentUser();
  const { applications, loading, error, reload } = useMyApplications();
  const approvalProgress = useApprovalProgress();
  const catalogStatus = useProductCatalogStatus();

  const pending = applications.filter((item) => item.status === "pending").length;
  const approved = applications.filter((item) => item.status === "approved").length;
  const rejected = applications.filter((item) => item.status === "rejected").length;
  const delistedCount = applications.filter((item) => isDelisted(catalogStatus.byName[item.product_name]?.status)).length;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 pb-10 md:px-6 md:pb-14">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950 md:text-2xl">
            {t("apply.myTitle")}
          </h2>
          <p className="mt-2 text-sm text-foreground-600">{t("apply.mySub")}</p>
        </div>
        {user && applications.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 font-medium text-accent-800">
              {t("apply.pendingLabel")} {pending}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700">
              {t("apply.approvedLabel")} {approved}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 font-medium text-secondary-900">
              {t("apply.rejectedLabel")} {rejected}
            </span>
          </div>
        )}
      </div>

      {!authLoading && !loading && user && delistedCount > 0 && (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-700">
            <i className="ri-forbid-2-line text-lg" />
          </span>
          <div>
            <p className="text-sm font-medium text-accent-900">
              {t("apply.delistedCountTitle", { count: delistedCount })}
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-accent-800">{t("apply.delistedCountDesc")}</p>
          </div>
        </div>
      )}

      <div className="mt-5">
        {authLoading || loading ? (
          <div className="flex flex-col gap-4">
            {[0, 1].map((item) => (
              <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="mt-4 h-8 w-full" />
                <Skeleton className="mt-4 h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : !user ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
              <i className="ri-lock-2-line text-2xl" />
            </span>
            <p className="text-sm font-medium text-foreground-800">{t("apply.loginTitle")}</p>
            <p className="max-w-sm text-xs text-foreground-500">{t("apply.loginDesc")}</p>
            <Link
              to="/auth?redirect=%2Fapply"
              className="mt-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-login-circle-line" />
              </span>
              {t("apply.loginBtn")}
            </Link>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-12 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-error-warning-line text-xl" />
            </span>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              type="button"
              onClick={reload}
              className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("apply.reloadProducts")}
            </button>
          </div>
        ) : applications.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <p className="text-sm font-medium text-foreground-800">{t("apply.emptyTitle")}</p>
            <p className="max-w-sm text-xs text-foreground-500">{t("apply.emptyDesc")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {applications.map((application) => {
              const catalogEntry = catalogStatus.byName[application.product_name];
              const delisted = isDelisted(catalogEntry?.status);
              const suggestions = delisted
                ? recommendSameCategory(catalogStatus.entries, catalogEntry?.category)
                : [];
              return (
                <ApplicationRow
                  key={application.id}
                  application={application}
                  delisted={delisted}
                  suggestions={suggestions}
                  category={catalogEntry?.category ?? null}
                  progress={approvalProgress.items[String(application.id)] ?? null}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}